import { useIsAuthenticated } from '@azure/msal-react';
import { IdManager } from '@celito.clients/singleton';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { HTTPStatusCode } from '../../enums/http-constants';
import { LayoutURLParamsEnum } from '../../enums/layout-url-enum';
import { ROUTES } from '../../enums/routes';
import { useActiveModule } from '../../hooks/useActiveModule';
import { useUser } from '../../hooks/useUser';
import { fetchHeaderConfig } from '../../services/header-services';
import { logger, refactorUser } from '../../services/logger';
import { getRolesInfo } from '../../services/roles-service';
import RolesContextManager from '../../singleton/roles-context-manager';
import UserContextManager from '../../singleton/user-context-manager';
import { HeaderConfig } from '../../types/header-config';
import { ModuleNamesEnum } from '../../types/module-types';
import { UserTypes } from '../../types/user-types';
import { getValueFromParams } from '../../utils/params.util';
import { raiseErrorToast } from '../../utils/toast-util';
import UserContext from './context';
import { DomainObject } from './types';

type props = {
  children: React.ReactNode;
};

const idManager = IdManager.getInstance();
const userContextManager = UserContextManager.getInstance();
const rolesContextManager = RolesContextManager.getInstance();

const UserProvider: React.FC<props> = ({ children }) => {
  const { fetchMyInfo, fetchUserPermission } = useUser();

  const [user, setUser] = React.useState<UserTypes | null>(null);
  const [headerConfig, setHeaderConfig] = React.useState<HeaderConfig | null>(
    null
  );

  const [isAdmin, setIsAdmin] = useState(false);
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(true);
  const [isBizAdmin, setIsBizAdmin] = useState(false);
  const [tabs, setTabs] = useState(['']);
  const [domainObjects, setDomainObjects] = useState<DomainObject[]>([]);
  const [roles, setRoles] = useState(['']);
  const lastTimeStamp = useRef('');

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const tenantManagementRoot = `/${ROUTES.TENANT_MANAGEMENT}`;
  const isTenantManagementRoute =
    location.pathname === tenantManagementRoot ||
    location.pathname.startsWith(`${tenantManagementRoot}/`);
  const detectedActiveModuleName =
    useActiveModule()?.systemName ??
    getValueFromParams(LayoutURLParamsEnum.moduleName, params);
  const activeModuleName = isTenantManagementRoute
    ? ModuleNamesEnum.ADMIN
    : detectedActiveModuleName;
  const isAuthenticated = useIsAuthenticated();

  const getRoles = useCallback(async () => {
    try {
      const roles = await getRolesInfo();

      rolesContextManager.setRoles(roles.data);
    } catch (error) {
      // handle error
    }
  }, []);

  const getUserDetails = useCallback(() => {
    fetchMyInfo()
      .then((user) => {
        logger.identity(user);
        logger.setContext(refactorUser(user));
        setUser(user);
        userContextManager.setUserContext(user);
        if (user?.name) {
          idManager.setId(user.name);
        }
      })
      .catch((err) => {
        if (err.response.body === 'User not invited') navigate('/not-invited');
        else if (err.response.body === 'User is deactivated')
          navigate('/not-active-user');
      });
  }, []);

  const getHeaderConfig = useCallback(() => {
    fetchHeaderConfig()
      .then((config) => {
        setHeaderConfig(config);
      })
      .catch((error) => {
        if (error?.response?.status >= HTTPStatusCode.NOT_FOUND)
          raiseErrorToast(error);
      });
  }, []);

  const getUserPermission = useCallback((moduleName: ModuleNamesEnum) => {
    setIsPermissionsLoading(true);
    fetchUserPermission('me', moduleName)
      .then(
        ({
          domainObjects,
          isAdmin,
          tabs,
          roles,
          isBizAdmin,
          permissionGroups,
        }) => {
          setIsAdmin(isAdmin);
          setTabs(tabs);
          setDomainObjects(domainObjects as DomainObject[]);
          setRoles(roles);
          setIsBizAdmin(isBizAdmin);

          userContextManager.setUserPermissionContext({
            isAdmin,
            tabs,
            domainObjects,
            roles,
            isBizAdmin,
            permissionGroups,
          });

          getHeaderConfig();
        }
      )
      .catch((error) => {
        if (error?.response?.status >= HTTPStatusCode.NOT_FOUND)
          raiseErrorToast(error);
      })
      .finally(() => setIsPermissionsLoading(false));
  }, []);

  useEffect(() => {
    // Platform administration is authorized only by the Entra SuperAdmin app role. It has no
    // tenant database, so never call tenant-only user/role endpoints from the platform host.
    if (isAuthenticated && !isTenantManagementRoute) {
      getUserDetails();
      getRoles();
    }
  }, [
    isAuthenticated,
    isTenantManagementRoute,
    getUserDetails,
    getHeaderConfig,
    getRoles,
  ]);

  useEffect(() => {
    if (!isTenantManagementRoute && user?.name && activeModuleName) {
      getUserPermission(activeModuleName as ModuleNamesEnum);
    }
  }, [activeModuleName, isTenantManagementRoute, user?.name]);

  const handleLastTimeStamp = (updatedTimeStamp: string) => {
    lastTimeStamp.current = updatedTimeStamp;
  };

  const value = {
    user,
    headerConfig,
    isAdmin,
    isPermissionsLoading,
    isBizAdmin,
    roles,
    tabs,
    domainObjects,
    lastTimeStamp: lastTimeStamp.current,
    handleLastTimeStamp,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserProvider;
